import type { IntelligenceTopicRecord } from "@graftus/schema";

import type { ChatScope } from "./scope";
import { isRecognizedTopicKey, type TopicKey } from "./topics";

/**
 * Pure resolution of the chat's active scope and topic.
 *
 * Extracted from `IntelligenceView` so the persistent coach rail (4a) and the
 * full-page `/coach` route resolve scope/topic identically. The rail reads its
 * raw inputs from a context provider; the page reads them from the URL — but the
 * derivation must stay in lock-step, so it lives here as a single source of
 * truth.
 */

type MinimalMember = { id: string; salesTeamId: string | null };

/**
 * The default scope for a user who cannot chat org-wide: the sidebar's active
 * team if visible, else the first team, else the first member. Returns `null`
 * while the teams/members lists are still loading (so callers can hold).
 */
export function deriveNonOrgDefaultScope(opts: {
  activeTeamScope: ChatScope | null;
  teamsLoading: boolean;
  teams: { id: string }[];
  membersLoading: boolean;
  members: MinimalMember[];
}): ChatScope | null {
  const { activeTeamScope, teamsLoading, teams, membersLoading, members } =
    opts;
  if (teamsLoading || membersLoading || (!teams.length && !members.length)) {
    return null;
  }
  if (activeTeamScope) return activeTeamScope;
  if (teams.length) return { kind: "team", salesTeamId: teams[0]!.id };
  const m = members[0]!;
  return { kind: "member", memberId: m.id, salesTeamId: m.salesTeamId };
}

/**
 * The active scope: derived from the loaded thread when one is open (scope is
 * pinned to the thread), otherwise from the URL/context scope param with the
 * sidebar's active-team selection as the implicit default.
 */
export function deriveChatScope(opts: {
  threadId: string | null;
  threadData:
    | { scope: string; salesTeamId: string | null; memberId: string | null }
    | null
    | undefined;
  membersById: Map<string, { salesTeamId: string | null }>;
  parsedScope: ChatScope;
  nonOrgDefaultScope: ChatScope | null;
  restrictToSubtree: boolean;
  scopeParam: string | null;
  activeTeamScope: ChatScope | null;
}): ChatScope {
  const {
    threadId,
    threadData,
    membersById,
    parsedScope,
    nonOrgDefaultScope,
    restrictToSubtree,
    scopeParam,
    activeTeamScope,
  } = opts;

  if (threadId && threadData) {
    if (threadData.scope === "team" && threadData.salesTeamId) {
      return { kind: "team", salesTeamId: threadData.salesTeamId };
    }
    if (threadData.scope === "member" && threadData.memberId) {
      const m = membersById.get(threadData.memberId);
      return {
        kind: "member",
        memberId: threadData.memberId,
        salesTeamId: m?.salesTeamId ?? threadData.salesTeamId,
      };
    }
    return { kind: "business" };
  }

  if (
    restrictToSubtree &&
    parsedScope.kind === "business" &&
    nonOrgDefaultScope
  ) {
    return nonOrgDefaultScope;
  }
  // Full-access users: with no explicit scope, follow the active team selected
  // in the sidebar. An explicit pick still wins.
  if (!scopeParam && parsedScope.kind === "business" && activeTeamScope) {
    return activeTeamScope;
  }
  return parsedScope;
}

/**
 * The active topic key: the thread's saved topic when recognized, else the
 * URL/context topic param, else the first available topic.
 */
export function deriveTopicKey(opts: {
  threadData: { topicKey?: string } | null | undefined;
  topicParam: string | null;
  topics: IntelligenceTopicRecord[];
}): TopicKey {
  const { threadData, topicParam, topics } = opts;
  if (
    threadData?.topicKey &&
    isRecognizedTopicKey(threadData.topicKey, topics)
  ) {
    return threadData.topicKey;
  }
  if (topicParam && isRecognizedTopicKey(topicParam, topics)) return topicParam;
  return topics[0]!.key;
}
